import * as React from 'react';
import PositionMap from '../PositionMap';

import MapWithInfo from '~/components/MapWithInfo';
import { observer } from 'mobx-react';
import Alert from '~store/Alert/Alert';
import { useQuery } from '@tanstack/react-query';
import { Device } from '@ixicare/resources';
import { useGPSGeofence, useLocation } from '~hooks/location';
import { formatDateTime } from '@ixicare/ui';
import { parseDate } from '~utils/date';

interface AlertMapContainerProps {
  alert: Alert;
}

const AlertMapContainer: React.FC<AlertMapContainerProps> = ({ alert }) => {
  if (!alert.isOpen) return <OldLocationMap alert={alert} />;
  return <CurrentLocationMap alert={alert} />;
};

function OldLocationMap({ alert }: AlertMapContainerProps) {
  const datetime = alert.acceptedTime ?? alert.cancelledTime ?? null;
  const deviceId = alert.device?.uuid ?? -1;
  let location = alert.device?.location;
  let locationTimestamp: Date | null = alert.created;
  if (!(alert.device?.locationIsStatic ?? false)) {
    location = useQuery({
      queryKey: ['device-location', deviceId, datetime?.toDateString()],
      queryFn: () => (datetime ? Device.getDeviceLocation(deviceId, datetime) : Device.getDeviceLastLocation(deviceId)),
      initialData: alert.device?.location ?? undefined,
    }).data;
    if (location?.lastKnown?.timestamp) {
      locationTimestamp = parseDate(location?.lastKnown?.timestamp);
    }
  }

  let info = formatDateTime(locationTimestamp);
  if (location?.lastKnown?.description) {
    info = location?.lastKnown?.description + ` (${info})`;
  }
  return (
    <MapWithInfo info={info}>
      <PositionMap
        locationData={location?.lastKnown ?? undefined}
        isStatic={location?.isStatic ?? false}
        type={alert.priority}
      />
    </MapWithInfo>
  );
}

function CurrentLocationMap({ alert }: AlertMapContainerProps) {
  const geo = useGPSGeofence(alert.resident?.id);

  const locationData = useLocation(alert.device?.id, alert.device?.location?.lastKnown ?? undefined);
  const info =
    (locationData?.description ?? '') +
    (locationData?.timestamp ? ` (${formatDateTime(parseDate(locationData?.timestamp))}` : '');
  return (
    <MapWithInfo info={info}>
      <PositionMap
        locationData={locationData}
        isStatic={alert.device?.locationIsStatic ?? false}
        type={alert.priority}
        geofence={geo}
      />
    </MapWithInfo>
  );
}

export default observer(AlertMapContainer);
